/**
 * Painel do dono do SaaS, servido em `admin.<domínio>`.
 *
 * É um app à parte do PWA das barbearias, e não por capricho de organização:
 * aqui não existe barbearia nenhuma. Não há tema de tenant para carregar, nem
 * `/api/tenant` para chamar — o escopo é a plataforma. Tentar reaproveitar a
 * casca do outro app significaria ensinar toda tela a viver sem tenant.
 *
 * O que ele resolve: colocar a segunda barbearia no ar sem abrir o banco.
 */

import type { AdminOverview, TenantAdminView, TenantStatus } from '@barbearia/shared';
import { TENANT_STATUS_LABELS } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Field,
  Sheet,
  Spinner,
} from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useRefreshSession, useSession } from '../../lib/contexts.tsx';
import { phone as formatPhone } from '../../lib/format.ts';

export function AdminApp() {
  const session = useSession();

  if (!session) return <AdminSignIn />;

  // Conta comum que descobriu o endereço: nada aqui é para ela.
  if (!session.user.isSuperAdmin) return <NotAllowed />;

  return <AdminHome />;
}

function AdminShell({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col">
      <header className="pt-safe sticky top-0 z-30 border-b border-line bg-surface/95 px-4 pb-3 backdrop-blur">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[12px] font-medium text-ink-faint">BarbearIA · plataforma</p>
            <h1 className="truncate text-[22px] leading-tight font-bold text-ink">{title}</h1>
            {subtitle && <p className="truncate text-[13px] text-ink-soft">{subtitle}</p>}
          </div>
          {action}
        </div>
      </header>
      <main className="flex-1 pb-10">{children}</main>
    </div>
  );
}

function NotAllowed() {
  const refresh = useRefreshSession();

  const signOut = useMutation({
    mutationFn: () => api.post('/auth/sign-out'),
    onSuccess: refresh,
  });

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 px-8 text-center">
      <h1 className="text-[20px] font-bold text-ink">Área restrita</h1>
      <p className="max-w-sm text-[15px] text-ink-soft">
        Esta conta não administra a plataforma. Se você é cliente ou barbeiro, entre pelo endereço
        da sua barbearia.
      </p>
      <Button variant="secondary" onClick={() => signOut.mutate()}>
        Sair
      </Button>
    </div>
  );
}

function AdminSignIn() {
  const refresh = useRefreshSession();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.post('/auth/sign-in', { identifier, password });
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Não deu certo');
      setLoading(false);
    }
  }

  return (
    <div className="pb-safe mx-auto flex min-h-full w-full max-w-sm flex-col justify-center gap-6 px-6 py-10">
      <div className="text-center">
        <h1 className="text-[22px] font-bold text-ink">BarbearIA</h1>
        <p className="pt-1 text-[14px] text-ink-soft">Administração da plataforma</p>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field
          label="Telefone ou usuário"
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          required
        />
        <Field
          label="Senha"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
        {error && <ErrorNote>{error}</ErrorNote>}
        <Button type="submit" full loading={loading}>
          Entrar
        </Button>
      </form>
    </div>
  );
}

function AdminHome() {
  const refresh = useRefreshSession();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TenantAdminView | null>(null);

  const { data: overview } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => api.get<{ overview: AdminOverview }>('/admin/overview'),
  });

  const { data, isPending } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: () => api.get<{ tenants: TenantAdminView[] }>('/admin/tenants'),
  });

  const signOut = useMutation({
    mutationFn: () => api.post('/auth/sign-out'),
    onSuccess: refresh,
  });

  const tenants = data?.tenants ?? [];

  return (
    <AdminShell
      title="Barbearias"
      subtitle={overview ? `${overview.overview.tenants} na plataforma` : undefined}
      action={
        <button
          type="button"
          onClick={() => signOut.mutate()}
          className="touch-target shrink-0 px-2 text-[15px] font-medium text-ink-soft"
        >
          Sair
        </button>
      }
    >
      {overview && (
        <section className="grid grid-cols-2 gap-2 px-4 pt-4 sm:grid-cols-4">
          <Stat label="Ativas" value={overview.overview.activeTenants} />
          <Stat label="Suspensas" value={overview.overview.suspendedTenants} />
          <Stat label="Contas" value={overview.overview.users} />
          <Stat label="Atendimentos 30d" value={overview.overview.appointmentsLast30Days} />
        </section>
      )}

      {isPending && <Spinner />}

      {!isPending && tenants.length === 0 && (
        <EmptyState
          title="Nenhuma barbearia ainda"
          description="Cadastre a primeira. Ela nasce em teste, com jornada de terça a sábado — o dono ajusta depois."
        />
      )}

      <ul className="flex flex-col gap-2 px-4 pt-4">
        {tenants.map((tenant) => (
          <li key={tenant.id}>
            <button
              type="button"
              onClick={() => setEditing(tenant)}
              className="w-full rounded-2xl border border-line bg-surface-raised px-4 py-3.5 text-left"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[16px] font-semibold text-ink">{tenant.name}</p>
                  <p className="truncate text-[13px] text-ink-faint">{tenant.url}</p>
                </div>
                <Badge tone={statusTone(tenant.status)}>
                  {TENANT_STATUS_LABELS[tenant.status]}
                </Badge>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 text-[13px] text-ink-soft">
                <span>{tenant.counts.staff} na equipe</span>
                <span>{tenant.counts.clients} clientes</span>
                <span>{tenant.counts.appointmentsLast30Days} atendimentos em 30d</span>
              </div>

              {tenant.owner && (
                <p className="pt-1 text-[13px] text-ink-faint">
                  {tenant.owner.name} · {formatPhone(tenant.owner.phone)}
                </p>
              )}
            </button>
          </li>
        ))}
      </ul>

      <div className="px-4 pt-4">
        <Button full onClick={() => setCreating(true)}>
          Cadastrar barbearia
        </Button>
      </div>

      {creating && <CreateTenantSheet onClose={() => setCreating(false)} />}
      {editing && <TenantSheet tenant={editing} onClose={() => setEditing(null)} />}
    </AdminShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-line bg-surface-raised px-3 py-2.5">
      <p className="text-[20px] leading-none font-bold tabular-nums text-ink">{value}</p>
      <p className="pt-1 text-[12px] text-ink-faint">{label}</p>
    </div>
  );
}

function statusTone(status: TenantStatus): 'ok' | 'warn' | 'danger' {
  if (status === 'ACTIVE') return 'ok';
  if (status === 'SUSPENDED') return 'danger';
  return 'warn';
}

/** Sugere o endereço a partir do nome, para ninguém digitar slug na mão. */
function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function CreateTenantSheet({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [created, setCreated] = useState<{ url: string; reused: boolean } | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ tenant: { url: string }; reusedExistingUser: boolean }>('/admin/tenants', {
        name,
        slug: effectiveSlug,
        owner: { name: ownerName, phone: ownerPhone, password },
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
      setCreated({ url: result.tenant.url, reused: result.reusedExistingUser });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  if (created) {
    return (
      <Sheet
        open
        onClose={onClose}
        title="Barbearia criada"
        footer={
          <Button full onClick={onClose}>
            Fechar
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-[15px] text-ink">Mande este endereço para o dono:</p>
          <p className="rounded-xl border border-primary bg-primary/10 px-4 py-3 text-[15px] font-medium break-all text-ink">
            {created.url}
          </p>

          {created.reused ? (
            <ErrorNote>
              Já existia uma conta com este telefone, então ela foi aproveitada e a senha que você
              digitou foi ignorada. O dono entra com a senha que já usava.
            </ErrorNote>
          ) : (
            <p className="text-[14px] text-ink-soft">
              Ele entra com o telefone e a senha que você definiu, e troca em Ajustes.
            </p>
          )}

          <p className="text-[13px] text-ink-faint">
            A conta nasce <strong>em teste</strong>, com jornada de terça a sábado (9h–12h e
            13h–19h). O dono ajusta expediente e serviços no próprio app.
          </p>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Nova barbearia"
      footer={
        <Button full loading={create.isPending} onClick={() => create.mutate()}>
          Cadastrar
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Nome da barbearia"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoCapitalize="words"
          placeholder="Barbearia do Zé"
          error={error?.fieldError('name')}
        />

        <Field
          label="Endereço"
          value={effectiveSlug}
          onChange={(event) => {
            setSlugTouched(true);
            setSlug(event.target.value);
          }}
          autoCapitalize="none"
          hint="É o subdomínio que a barbearia vai divulgar"
          error={error?.fieldError('slug')}
        />

        <div className="border-t border-line pt-4">
          <p className="pb-3 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Dono
          </p>

          <div className="flex flex-col gap-4">
            <Field
              label="Nome"
              value={ownerName}
              onChange={(event) => setOwnerName(event.target.value)}
              autoCapitalize="words"
              error={error?.fieldError('owner.name')}
            />
            <Field
              label="Telefone"
              value={ownerPhone}
              onChange={(event) => setOwnerPhone(event.target.value)}
              inputMode="tel"
              placeholder="(51) 99999-8888"
              error={error?.fieldError('owner.phone')}
            />
            <Field
              label="Senha inicial"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              hint="Mínimo 8 caracteres. Ele troca depois, em Ajustes"
              error={error?.fieldError('owner.password')}
            />
          </div>
        </div>

        {error && !error.fields.slug && <ErrorNote>{error.message}</ErrorNote>}
      </div>
    </Sheet>
  );
}

function TenantSheet({ tenant, onClose }: { tenant: TenantAdminView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.patch(`/admin/tenants/${tenant.id}`, patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  /**
   * "O dono da barbearia esqueceu a senha."
   *
   * Ele é o cliente pagante e não tem a quem pedir: o link de nova senha da
   * barbearia só serve para clientes, e o dono está acima de todo mundo lá
   * dentro. Até existir este botão, a resposta era SQL na mão.
   *
   * Não fecha a gaveta ao terminar, ao contrário das outras ações: quem clicou
   * precisa continuar vendo o telefone do dono na tela para conferir que a
   * conversa que abriu é a certa.
   */
  const ownerResetLink = useMutation({
    mutationFn: () =>
      api.post<{ whatsapp: { url: string } }>(`/admin/tenants/${tenant.id}/owner-reset-link`),
    onSuccess: (result) => {
      setError(null);
      window.open(result.whatsapp.url, '_blank');
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Não foi possível gerar o link'),
  });

  return (
    <Sheet open onClose={onClose} title={tenant.name}>
      <div className="flex flex-col gap-5">
        <dl className="overflow-hidden rounded-xl border border-line">
          <Row label="Endereço" value={tenant.url} />
          <Row label="Situação" value={TENANT_STATUS_LABELS[tenant.status]} />
          <Row label="Plano" value={tenant.plan} />
          <Row label="Fuso" value={tenant.timezone} />
          <Row label="Equipe" value={String(tenant.counts.staff)} />
          <Row label="Clientes" value={String(tenant.counts.clients)} />
          <Row label="Serviços" value={String(tenant.counts.services)} />
          <Row label="Atendimentos (30d)" value={String(tenant.counts.appointmentsLast30Days)} />
          {tenant.owner && (
            <Row label="Dono" value={`${tenant.owner.name} · ${formatPhone(tenant.owner.phone)}`} />
          )}
        </dl>

        {tenant.counts.appointmentsLast30Days === 0 && tenant.status !== 'SUSPENDED' && (
          <div className="rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-[14px] text-ink">
            Nenhum atendimento em 30 dias. Vale ligar antes de cobrar.
          </div>
        )}

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex flex-col gap-2">
          {tenant.status !== 'ACTIVE' && (
            <Button
              full
              loading={update.isPending}
              onClick={() => update.mutate({ status: 'ACTIVE' })}
            >
              Ativar barbearia
            </Button>
          )}

          {tenant.status !== 'SUSPENDED' ? (
            <Button
              variant="danger"
              full
              loading={update.isPending}
              onClick={() => update.mutate({ status: 'SUSPENDED' })}
            >
              Suspender acesso
            </Button>
          ) : (
            <p className="text-[13px] text-ink-faint">
              Suspender já derrubou as sessões da equipe. Reativar libera o acesso na hora.
            </p>
          )}

          <a href={tenant.url} target="_blank" rel="noreferrer">
            <Button variant="secondary" full>
              Abrir a barbearia
            </Button>
          </a>

          {tenant.owner && (
            <div className="pt-2">
              <Button
                variant="secondary"
                full
                loading={ownerResetLink.isPending}
                onClick={() => ownerResetLink.mutate()}
              >
                Mandar nova senha para {tenant.owner.name.split(' ')[0]}
              </Button>
              <p className="pt-1.5 text-[13px] text-ink-faint">
                Abre o WhatsApp de {formatPhone(tenant.owner.phone)} com um link que vale por 1
                hora. A senha atual continua valendo até ele criar a nova.
              </p>
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-2.5 last:border-b-0">
      <dt className="shrink-0 text-[13px] text-ink-faint">{label}</dt>
      <dd className="truncate text-[14px] font-medium text-ink">{value}</dd>
    </div>
  );
}
